import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CalendarDays, HelpCircle, Loader2, Paperclip, Plus, Receipt, Trash2, Wallet } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate, formatMoney } from '../lib/utils';
import {
  Card, Disclosure, EmptyState, FileInput, ICON, IconButton, Kpi, ListRow, Notice, PageHeader, PageSkeleton, Sheet,
} from '../components/ui';

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'parkeer', label: 'Parkeerkosten' },
  { value: 'maaltijd', label: 'Maaltijd' },
  { value: 'stomerij', label: 'Stomerij uniform' },
  { value: 'tanken', label: 'Tanken' },
  { value: 'ov', label: 'Openbaar vervoer' },
  { value: 'materiaal', label: 'Materiaal / kleding' },
  { value: 'telefonie', label: 'Telefoon / data' },
  { value: 'overig', label: 'Overig' },
];

interface ExpenseRow {
  id: number;
  expense_date: string;
  category: string;
  description: string | null;
  amount: number;
  shift_id: number | null;
  receipt_r2_key: string | null;
  submitted_at: string | null;
  reimbursed_at: string | null;
  reimbursed_amount: number | null;
}

/** Wat je volgens de CAO Particuliere Beveiliging (2024-2026) zelf declareert. */
const CAO_EXAMPLES: Array<{ title: string; text: string; art: string }> = [
  {
    title: 'Maaltijd bij een lange dienst',
    text: 'Dienst begint vóór 13:00 en eindigt na 19:00, of loopt minstens 2 uur uit: maximaal € 11,91 (bedrag 2025, jaarlijks geïndexeerd).',
    art: 'art. 48',
  },
  {
    title: 'Stomerij voor je uniform',
    text: 'Minimaal 1 keer per 4 weken laten stomen; de kosten declareer je.',
    art: 'art. 52',
  },
  {
    title: 'Parkeren',
    text: 'Alleen als er binnen 1,5 km van je werkplek geen gratis parkeerplek is én geen ov dat minstens 1 keer per uur rijdt.',
    art: 'art. 47 lid 8',
  },
  {
    title: 'Tol, veerpont en openbaar vervoer',
    text: 'Tol of veerpont op de route van de reiskostenregeling, en treinkaartjes (2e klas) als je met het ov reist. Bewaar de vervoersbewijzen.',
    art: 'art. 47 lid 2 en 7',
  },
];

const emptyForm = () => ({
  expense_date: new Date().toISOString().slice(0, 10),
  category: 'parkeer',
  description: '',
  amount: '',
  shift_id: '' as string | number,
});

const maandNaam = (ym: string) => {
  const t = new Date(ym + '-01').toLocaleDateString('nl-NL', { year: 'numeric', month: 'long' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function OnkostenPage() {
  const qc = useQueryClient();
  const { data: list = [], isLoading, error } = useQuery<ExpenseRow[]>({
    queryKey: ['expenses'],
    queryFn: api.expenses.list,
  });

  const [open, setOpen] = useState(false);

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.expenses.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['expenses'] }),
  });

  const totalsPerMonth = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of list) {
      const key = e.expense_date.slice(0, 7); // YYYY-MM
      map.set(key, (map.get(key) ?? 0) + e.amount);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 12);
  }, [list]);

  const thisMonth = new Date().toISOString().slice(0, 7);
  const grandTotal = useMemo(() => list.reduce((sum, e) => sum + e.amount, 0), [list]);
  const monthTotal = useMemo(
    () => list.filter((e) => e.expense_date.startsWith(thisMonth)).reduce((sum, e) => sum + e.amount, 0),
    [list, thisMonth]
  );
  const zonderBon = list.filter((e) => !e.receipt_r2_key).length;

  const categoryMap = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label])), []);

  if (isLoading) return <PageSkeleton kpis={3} />;

  const addButton = (
    <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
      <Plus className={ICON.sm} aria-hidden />
      Kosten toevoegen
    </button>
  );

  return (
    <div className="page">
      <PageHeader
        title="Onkosten"
        sub="Kosten die je voor je werk maakte, met de bon erbij. Zo weet je wat je nog declareert."
        actions={addButton}
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        <Kpi
          icon={Wallet}
          label="Totaal bijgehouden"
          value={grandTotal}
          format={formatMoney}
          hint={`${list.length} ${list.length === 1 ? 'post' : 'posten'}`}
        />
        <Kpi icon={CalendarDays} label="Deze maand" value={monthTotal} format={formatMoney} hint={maandNaam(thisMonth)} />
        <Kpi
          icon={Receipt}
          label="Zonder bon"
          value={zonderBon}
          hint={zonderBon ? 'Voeg de bon toe als je hem nog hebt' : 'Alles heeft een bon'}
          className="col-span-2 lg:col-span-1"
        />
      </div>

      {error && <Notice tone="err">De onkosten konden niet laden: {(error as Error).message}</Notice>}

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-3 [&>*]:min-w-0">
        <Card flush title="Alle onkosten" className={cn(totalsPerMonth.length > 0 && 'lg:col-span-2', totalsPerMonth.length === 0 && 'lg:col-span-3')}>
          {list.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="Nog niets bijgehouden"
              text="Parkeren, stomerij of een maaltijd bij een lange dienst: zet het hier met de bon erbij."
              action={addButton}
            />
          ) : (
            <div className="list border-t border-surface-line/60">
              {list.map((e, i) => (
                <div key={e.id} className="row enter" style={{ '--i': i } as React.CSSProperties}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm text-slate-100">{categoryMap[e.category] ?? e.category}</div>
                    <div className="truncate text-xs text-slate-400">
                      <span className="t-num">{formatDate(e.expense_date)}</span>
                      {e.description && <>, {e.description}</>}
                    </div>
                  </div>
                  {e.receipt_r2_key ? (
                    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-slate-400" title="Bon opgeslagen">
                      <Paperclip className={ICON.sm} aria-label="Bon opgeslagen" />
                      <span className="hidden sm:inline">Bon</span>
                    </span>
                  ) : (
                    <span className="hidden shrink-0 text-xs text-slate-500 sm:inline">Geen bon</span>
                  )}
                  <span className="t-num shrink-0 text-right text-sm font-medium text-slate-50 sm:w-24">{formatMoney(e.amount)}</span>
                  <IconButton
                    icon={Trash2}
                    label="Verwijderen"
                    disabled={deleteMut.isPending}
                    className="-mr-2 shrink-0 hover:text-red-300"
                    onClick={() => {
                      if (confirm('Deze onkosten verwijderen?')) deleteMut.mutate(e.id);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>

        {totalsPerMonth.length > 0 && (
          <Card flush title="Per maand">
            <div className="list border-t border-surface-line/60">
              {totalsPerMonth.map(([month, total], i) => (
                <ListRow key={month} title={maandNaam(month)} value={formatMoney(total)} i={i} />
              ))}
            </div>
          </Card>
        )}
      </div>

      <Card flush>
        <Disclosure icon={HelpCircle} title="Wanneer declareer je iets?" meta="Wat je volgens de CAO zelf terugvraagt">
          <Declareren />
        </Disclosure>
      </Card>

      <ExpenseSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

/** Uitleg: wat declareer je zelf, en waar. */
function Declareren() {
  return (
    <div className="space-y-3 text-sm">
      <p className="text-slate-300">
        Je reiskosten woon-werk (€ 0,23 per km vanaf 9 km) staan al op je loonstrook. Deze kosten declareer je wel zelf:
      </p>
      <ul className="list -mx-4 sm:-mx-5">
        {CAO_EXAMPLES.map((ex) => (
          <li key={ex.title} className="px-4 py-2.5 sm:px-5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="font-medium text-slate-100">{ex.title}</span>
              <span className="shrink-0 text-xs text-slate-500">CAO {ex.art}</span>
            </div>
            <p className="mt-0.5 text-slate-400">{ex.text}</p>
          </li>
        ))}
      </ul>
      <p className="text-slate-400">
        De app verstuurt niets. Je dient de declaratie zelf in via het medewerkersportaal van Trigion (AFAS InSite), met de bon als bijlage.
      </p>
    </div>
  );
}

/** Formulier in een paneel: kosten toevoegen met optioneel een bon. */
function ExpenseSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState<File | null>(null);

  const { data: recentShifts = [] } = useQuery({
    queryKey: ['shifts-last-180'],
    queryFn: () => {
      const to = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 180 * 86_400_000).toISOString().slice(0, 10);
      return api.shifts.list(from, to);
    },
    enabled: open,
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const amount = Number(String(form.amount).replace(',', '.'));
      if (!amount || amount <= 0) throw new Error('Vul een bedrag hoger dan € 0 in.');
      const res = await api.expenses.create({
        expense_date: form.expense_date,
        category: form.category,
        description: form.description || null,
        amount,
        shift_id: form.shift_id ? Number(form.shift_id) : null,
      });
      if (file) {
        await api.expenses.uploadReceipt(res.id, file);
      }
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      setForm(emptyForm());
      setFile(null);
      onClose();
    },
  });

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Kosten toevoegen"
      sub="Bewaar de bon; je declareert zelf via AFAS InSite."
      footer={
        <button type="button" className="btn-primary w-full" disabled={createMut.isPending} onClick={() => createMut.mutate()}>
          {createMut.isPending && <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden />}
          Opslaan
        </button>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          createMut.mutate();
        }}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <label className="label" htmlFor="exp-date">Datum</label>
            <input
              id="exp-date"
              type="date"
              className="input"
              value={form.expense_date}
              onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
            />
          </div>
          <div className="min-w-0">
            <label className="label" htmlFor="exp-amount">Bedrag (€)</label>
            <input
              id="exp-amount"
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              className="input tabular-nums"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="exp-cat">Soort</label>
          <select id="exp-cat" className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label" htmlFor="exp-desc">Omschrijving</label>
          <input
            id="exp-desc"
            type="text"
            className="input"
            placeholder="Bijvoorbeeld: parkeergarage Den Haag"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>

        <div>
          <label className="label" htmlFor="exp-shift">Bij dienst (optioneel)</label>
          <select id="exp-shift" className="input" value={form.shift_id} onChange={(e) => setForm({ ...form, shift_id: e.target.value })}>
            <option value="">Geen</option>
            {recentShifts.map((s) => (
              <option key={s.id} value={s.id}>
                {formatDate(s.shift_date)} {s.start_time}-{s.end_time}
                {s.object?.naam ? `, ${s.object.naam}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <span className="label">Bon (pdf of foto)</span>
          <FileInput accept="application/pdf,image/*" file={file} onChange={setFile} label="Bon kiezen" />
        </div>

        {createMut.isError && (
          <Notice tone="err">
            <span className="inline-flex items-start gap-2">
              <AlertCircle className={cn(ICON.sm, 'mt-0.5 shrink-0')} aria-hidden />
              {(createMut.error as Error).message}
            </span>
          </Notice>
        )}

        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Sheet>
  );
}

export default OnkostenPage;
